// Rôles côté front — une seule source pour « qui atterrit où » et « qui peut
// ouvrir quoi ». Le serveur reste l'autorité (api/app/middleware/roleGate.js :
// 2FA obligatoire par rôle, rôle comptable en lecture seule) ; ceci n'est que
// le confort de navigation. Les rôles arrivent du login sous la forme
// « ROLE_ACCOUNTANT,ROLE_… » dans localStorage['login-roles'] (tests par
// sous-chaîne, comme partout dans l'application).

// Posé par le login quand le serveur ne rend qu'un token de configuration 2FA
// (rôle à 2FA obligatoire) : tant qu'il est là, seule /pages/2fa-setup est ouverte.
export const TWOFA_SETUP_FLAG = 'xylo:twofa-setup-required'
// Message à afficher à l'arrivée sur la page de login (posé par l'intercepteur 401).
export const LOGIN_NOTICE_KEY = 'xylo:login-notice'

export const ACCOUNTANT_HOME = '/reports/centralizator'
// Portail client (rôle « client ») : ses pages vivent sous /client, avec leur
// propre gabarit (layouts/client/ClientLayout.vue), sans le menu de l'ERP.
export const CLIENT_HOME = '/client'

// Pages ouvertes à une comptable (sans admin/moderator) : son menu, les fiches
// qu'il ouvre et sa fiche de profil. Les pages d'édition (/invoice-edit,
// /reception-add, /outlay-edit, /firewood-invoice-edit, …) ne commencent pas
// par ces préfixes.
const ACCOUNTANT_PREFIXES = [
  '/efacturalist-ing',        // e-Factura entrantes (Achiziții)
  '/efacturalist-out',        // e-Factura sortantes (Vânzări)
  '/outlay/',                 // liste des dépenses + fiche (ouverte par l'œil e-Factura)
  '/outlayboneur/',           // fiche d'un bon en EUR
  '/invoice/',                // liste + fiche facture
  '/firewood-invoice/',       // fiche facture bois de chauffage (/invoice/:id y redirige)
  '/reception/',              // liste + fiche réception
  '/production/',             // liste + fiche production + analyse
  '/stock/',                  // liste + fiche stock
  '/reports/',                // section Rapoarte
  '/configurations/edit-profil'
]
const ACCOUNTANT_EXACT = [
  '/pages/login',
  '/pages/2fa-setup',
  '/pages/not-authorized',
  '/pages/error-404',
  '/pages/error-500',
  '/pages/lock-screen'
]
// Pages d'écriture rangées sous un préfixe autorisé.
const ACCOUNTANT_DENY = ['/reception/copyproduct/', '/outlay/dashboard']

function normalizePath (path) {
  let p = String(path || '').split('?')[0].split('#')[0].replace(/^\/web(?=\/|$)/, '')
  if (p.length > 1) p = p.replace(/\/+$/, '')
  return p || '/'
}

export function rolesString () {
  try { return (localStorage.getItem('login-roles') || '').toLowerCase() } catch (e) { return '' }
}

function rolesOf (roles) {
  return roles === undefined || roles === null ? rolesString() : String(roles).toLowerCase()
}

export function hasRole (name, roles) {
  return rolesOf(roles).includes(String(name).toLowerCase())
}

export function isAdminLike (roles) {
  const r = rolesOf(roles)
  return r.includes('admin') || r.includes('moderator')
}

export function isAccountantOnly (roles) {
  return hasRole('accountant', roles) && !isAdminLike(roles)
}

// Test exact du rôle : « client » est aussi une sous-chaîne d'autres noms.
export function isClientOnly (roles) {
  const list = rolesOf(roles).split(',').map(s => s.trim().replace(/^role_/, ''))
  return list.includes('client') && !isAdminLike(roles)
}

// Section Rapoarte : admins, modérateurs, comptable.
export function canSeeReports (roles) {
  return isAdminLike(roles) || hasRole('accountant', roles)
}

// Page d'atterrissage après connexion. Admin/moderator d'abord : un admin qui
// cumule un autre rôle garde le tableau de bord.
export function landingPathFor (roles) {
  const r = rolesOf(roles)
  if (r.includes('admin') || r.includes('moderator')) return '/'
  if (isClientOnly(r)) return CLIENT_HOME
  if (r.includes('accountant')) return ACCOUNTANT_HOME
  if (r.includes('attendance')) return '/employees/attendance'
  if (r.includes('broker')) return '/broker/dashboard'
  return '/'
}

export function isPathAllowedForAccountant (path) {
  const p = normalizePath(path)
  if (ACCOUNTANT_DENY.some(d => p.startsWith(d))) return false
  if (ACCOUNTANT_EXACT.includes(p)) return true
  return ACCOUNTANT_PREFIXES.some(pre => p === pre || p.startsWith(pre))
}

// Un compte client ne sort jamais de /client (le serveur lui refuse de toute
// façon tout le reste de l'API : roleGate, 403 client_scope).
export function isPathAllowedForClient (path) {
  const p = normalizePath(path)
  if (ACCOUNTANT_EXACT.includes(p)) return true
  return p === CLIENT_HOME || p.startsWith(`${CLIENT_HOME}/`)
}

// Lien profond (?to=) acceptable pour ces rôles après connexion.
export function isPathAllowedForRoles (path, roles) {
  if (isClientOnly(roles)) return isPathAllowedForClient(path)
  if (isAccountantOnly(roles)) return isPathAllowedForAccountant(path)
  return true
}

// --- Compte en lecture seule (users.read_only, switch Broker Check → Broker Status) ---
// Le serveur refuse toute écriture (roleGate, 403 read_only_user) et pose
// l'en-tête x-read-only (1/0) sur chaque réponse gardée ; ici on tient le
// drapeau de session à jour pour le bandeau, les boutons et la navigation.
export const READ_ONLY_FLAG = 'login-readonly'
export const READ_ONLY_EVENT = 'xylo:readonly-changed'
export const READ_ONLY_TEXT = 'Your account is in read-only mode: you can view your quotations, invoices and contacts, but changes are disabled.'

export function isReadOnlyUser () {
  try { return localStorage.getItem(READ_ONLY_FLAG) === '1' } catch (e) { return false }
}

export function setReadOnlyUser (on) {
  const next = on ? '1' : '0'
  let prev = null
  try {
    prev = localStorage.getItem(READ_ONLY_FLAG)
    localStorage.setItem(READ_ONLY_FLAG, next)
  } catch (e) { return }
  if (prev !== next && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try { window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: { readOnly: !!on } })) } catch (e) { /* ignore */ }
  }
}

// En-tête posé par roleGate : absent (route publique, token scopé) = pas de changement.
export function applyReadOnlyHeader (headers) {
  if (!headers) return
  const v = headers['x-read-only']
  if (v === '1' || v === '0') setReadOnlyUser(v === '1')
}

// Un seul toast par tranche de 5 s : une page peut tenter plusieurs écritures d'un coup.
let lastReadOnlyNoticeAt = 0
export function notifyReadOnlyOnce (vm, text) {
  const now = Date.now()
  if (now - lastReadOnlyNoticeAt < 5000) return
  lastReadOnlyNoticeAt = now
  if (!vm || !vm.$vs || typeof vm.$vs.notify !== 'function') return
  vm.$vs.notify({
    title: 'Read-only mode',
    text: text || READ_ONLY_TEXT,
    color: 'warning',
    iconPack: 'feather',
    icon: 'icon-lock',
    position: 'bottom-right',
    time: 8000
  })
}

// Pages d'ajout / d'édition du portail broker : renvoyées vers la liste correspondante.
const READ_ONLY_BLOCKED = /^\/broker\/(quotation-add|quotation-add-idpers|quotation-edit|invoice-edit|person-add|person-edit)(\/|$)/

export function isReadOnlyBlockedPath (path) {
  return READ_ONLY_BLOCKED.test(normalizePath(path))
}

export function readOnlyRedirectFor (path) {
  const p = normalizePath(path)
  if (p.startsWith('/broker/invoice-edit')) return '/broker/invoice/invoice-list'
  if (p.startsWith('/broker/person-')) return '/broker/people/people-list'
  return '/broker/quotation/quotation-list'
}
